<script setup lang="ts" generic="T">
import type {
  IVirtualizedTreeExpose,
  IVirtualizedTreeNode,
  IVirtualizedTreeNodeModel,
} from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.types";

import VirtualizedList from "#/ui-kit/components/ui/virtualized-list/virtualized-list.vue";
import { useVirtualizedTreeController } from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.controller";
import { useTemplateRef } from "vue";

export interface IVirtualizedTreeProps<T> {
  nodes: IVirtualizedTreeNode<T>[];
  /**
   * heights in px
   */
  viewportHeight?: number;
  itemHeight?: number;
}

const props = withDefaults(defineProps<IVirtualizedTreeProps<T>>(), {
  viewportHeight: 800,
  itemHeight: 40,
});
defineSlots<{
  padding: () => unknown;
  item: (props: {
    index: number;
    item: IVirtualizedTreeNodeModel<T>;
    handleToggleNode: (node: IVirtualizedTreeNodeModel<T>) => Promise<void>;
  }) => unknown;
}>();

const virtualizedList = useTemplateRef("virtualizedList");

const { flattenedTree, toggleNode, refreshFlattenedTree } = await useVirtualizedTreeController<
  T,
  IVirtualizedTreeNode<T>,
  IVirtualizedTreeNodeModel<T>
>(() => props.nodes);
let latestRefreshPromise: Promise<void> | undefined;

async function handleToggleNode(node: IVirtualizedTreeNodeModel<T>) {
  await toggleNode(node);
  virtualizedList.value?.refreshView();
}

function scrollToIndex(index: number) {
  virtualizedList.value?.scrollToIndex(index);
}

function scrollToNode(findNodeFn: (node: IVirtualizedTreeNodeModel<T>) => boolean) {
  virtualizedList.value?.scrollToNode(findNodeFn);
}

async function waitForLatestRefresh(refreshPromise: Promise<void>): Promise<void> {
  await refreshPromise;

  while (latestRefreshPromise && refreshPromise !== latestRefreshPromise) {
    refreshPromise = latestRefreshPromise;
    await refreshPromise;
  }
}

function forceUpdate(): Promise<void> {
  const refreshPromise = refreshFlattenedTree().then(() => virtualizedList.value?.refreshView());
  latestRefreshPromise = refreshPromise;
  return waitForLatestRefresh(refreshPromise);
}

defineExpose<IVirtualizedTreeExpose<IVirtualizedTreeNodeModel<T>>>({
  forceUpdate,
  scrollToNode,
  scrollToIndex,
});
</script>

<template>
  <VirtualizedList
    ref="virtualizedList"
    :items="flattenedTree"
    :viewport-height="props.viewportHeight"
    :item-height="props.itemHeight"
    class="virtualized-tree"
  >
    <template #item="{ item, index }">
      <div class="virtualized-tree-item-container flex items-center h-full w-full">
        <template
          v-for="(_parent, _parentIdx) in item.parentsIndexes"
          :key="_parentIdx"
        >
          <slot name="padding">
            <div class="tree-node-padding" />
          </slot>
        </template>

        <slot
          :index="index"
          :item="item"
          name="item"
          :handleToggleNode="handleToggleNode"
        >
          <div class="flex items-center gap-2 w-full">
            <i
              :class="[
                'toggle-node-icon pi mx-2',
                item.state.isExpanded ? 'pi-angle-down' : 'pi-angle-right',
                item.children.length ? 'with-children' : 'without-children',
              ]"
              @click="handleToggleNode(item)"
            />
            <div class="tree-node-item">
              {{ item.name }}
            </div>
          </div>
        </slot>
      </div>
    </template>
  </VirtualizedList>
</template>

<style scoped lang="scss">
.virtualized-tree {
  .tree-node-padding {
    @apply h-full border-l;
    margin-left: 2rem;
  }

  .toggle-node-icon {
    &.with-children {
      cursor: pointer;
    }

    &.without-children {
      opacity: 0;
    }
  }
}
</style>
